import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import type {RootState} from '../app/store'
import {RRCalendarDataType, RRCalendarDateDataType} from "../app/types/RRCalendarDataType";
import {RRCalendarDaysType} from "../app/types/RRCalendarDayType";
import {dateToString, dateWithZeroTime} from "../app/tools";
import {RREventsType, RREventType,} from "../app/types/RREventType";
import {Maybe} from "../app/types/Maybe";
import {RREditCalendarEventType} from "../app/types/RREditCalendarEventType";
import {AppUserType} from "../app/types/AppUserType";
import {RREmployeeType} from "../app/types/RREmployeeType";
import {RRPrintCalendarEventType} from "../app/types/RRPrintCalendarEventType";

export type RRDepartmentType = "" | "montage" | "catering" | "store" | "logistics";
export const departmentNames = {
    "": "не е зададено",
    "montage": "Монтаж",
    "catering": "Кетъринг",
    "store": "Кухня",
    "logistics": "Транспорт",
};

export type AppStateType = {
    currentUser?: AppUserType;
    currentEmployee?: RREmployeeType;
    selectedEmployees: string[];
    department: RRDepartmentType,
    calendarFirstDate: Date;
    visibleDays: number;
    calendarData: RRCalendarDataType;
    employeeCalendarData: RRCalendarDataType;
    editCalendarEvent: Maybe<RREditCalendarEventType>;
    printCalendarEvent: Maybe<RRPrintCalendarEventType>;
    reportCalendarEvent: Maybe<RREditCalendarEventType>;

    isEmployeesPopupVisible: boolean;
    isApprovePPHPopupVisible: boolean;
    isEventsPopupVisible: boolean;
    isLogisticsPopupVisible: boolean;
    preselectedEvent?: RREventType;
}

const initialState: AppStateType = {
    department: "",
    calendarFirstDate: new Date(),
    visibleDays: 7,
    calendarData: [],
    employeeCalendarData: [],
    editCalendarEvent: null,
    printCalendarEvent: null,
    reportCalendarEvent: null,
    isEventsPopupVisible: false,
    isLogisticsPopupVisible: false,
    isApprovePPHPopupVisible: false,
    isEmployeesPopupVisible: false,
    selectedEmployees: [],
}


export function getDayEvents(date: Date, events: RREventsType): RREventsType {
    return events.filter(e => {
        if (e.id === "WAREHOUSE") return true;

        const currDay = dateWithZeroTime(date)!;
        const firstDay = dateWithZeroTime(e.firstDate) || currDay;
        const lastDay = dateWithZeroTime(e.lastDate) || firstDay;

        return (firstDay <= currDay && lastDay >= currDay);
    });
}

export const appStateSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        setCurrentUser: (state, action: PayloadAction<AppUserType | undefined>) => {
            state.currentUser = action.payload;
        },

        setCurrentEmployee: (state, action: PayloadAction<RREmployeeType | undefined>) => {
            state.currentEmployee = action.payload;
        },

        setCalendarFirstDate: (state, action: PayloadAction<Date>) => {
            state.calendarFirstDate = action.payload;
        },

        setDepartment: (state, action: PayloadAction<RRDepartmentType>) => {
            state.department = action.payload;
        },

        setCalendarVisibleDays: (state, action: PayloadAction<number>) => {
            state.visibleDays = action.payload < 1 ? 1 : action.payload;
        },

        setCurrentCalendarData: (state, action: PayloadAction<{ data: RRCalendarDaysType, events: RREventsType }>) => {
            const data = new Map<string, RRCalendarDateDataType>();
            const events = action.payload.events.filter(
                value => value.eventStatus !== "ОТКАЗАНО"
            );

            const dateCursor = new Date(state.calendarFirstDate);
            const lastDate = new Date(dateCursor);
            lastDate.setDate(lastDate.getDate() + state.visibleDays - 1);

            while (dateCursor <= lastDate) {
                const item: RRCalendarDateDataType = {
                    date: dateToString(dateCursor),
                    events: getDayEvents(dateCursor, events),
                    items: [],
                }
                data.set(item.date, item);
                dateCursor.setDate(dateCursor.getDate() + 1);
            }

            action.payload.data.forEach(
                value => {
                    const date = dateToString(value.eventDayStart);
                    if (data.has(date)) {
                        const dataItem: RRCalendarDateDataType | undefined = data.get(date);
                        if (dataItem) {
                            // let event = dataItem.events.find(
                            //     value1 => value1.id === value.eventId
                            // );
                            //
                            // if (!event) {
                            //     event = events.find(
                            //         value1 => value1.id === value.eventId
                            //     );
                            //     if (event) {
                            //         dataItem.events.push(event);
                            //     }
                            // }
                            // // if (event) {
                            // //     dataItem.items.push(value);
                            // // }
                            dataItem.items.push(value);
                        }
                    }
                }
            )


            const sortedData = Array.from(data.values()).map(
                value => {
                    const events = [...value.events];
                    events.sort(
                        (a, b) => {
                            let idx = 0;
                            if (a.id === "WAREHOUSE") idx = 1; else {
                                if (b.id === "WAREHOUSE") idx = -1;
                                else idx = (a.firstDate! > b.firstDate!) ? 1 : (a.firstDate! < b.firstDate! ? -1 : 0);
                            }
                            return idx;
                        }
                    );
                    return {...value, events: events};
                });

            state.calendarData = sortedData;
        },

        setEditCalendarEvent: (state,
                               action: PayloadAction<Maybe<RREditCalendarEventType>>) => {
            state.editCalendarEvent = action.payload;
        },

        setPrintCalendarEvent: (state,
                                action: PayloadAction<Maybe<RRPrintCalendarEventType>>) => {
            state.printCalendarEvent = action.payload;
        },

        setReportCalendarEvent: (state,
                                 action: PayloadAction<Maybe<RREditCalendarEventType>>) => {
            state.reportCalendarEvent = action.payload;
        },

        setEmployeesPopupVisible: (state, action: PayloadAction<boolean>) => {
            state.isEmployeesPopupVisible = action.payload;
        },

        setLogisticsPopupVisible: (state, action: PayloadAction<boolean>) => {
            state.isLogisticsPopupVisible = action.payload;
        },

        setApprovePPHPopupVisible: (state, action: PayloadAction<boolean>) => {
            state.isApprovePPHPopupVisible = action.payload;
        },

        setEventsPopupVisible: (state, action: PayloadAction<boolean>) => {
            state.isEventsPopupVisible = action.payload;
            state.preselectedEvent = undefined;
        },

        setPreselectedEvent: (state, action: PayloadAction<RREventType>) => {
            state.isEventsPopupVisible = true;
            state.preselectedEvent = action.payload;
        },

        selectEmployee: (state, action: PayloadAction<string | string[]>) => {
            let ids: string[] = [];
            if (typeof action.payload === "string") ids.push(action.payload)
            else ids = [...action.payload];

            ids.forEach(
                id => {
                    const idx = state.selectedEmployees.indexOf(id);
                    if (idx === -1) {
                        state.selectedEmployees.push(id);
                    }
                }
            )
        },

        deselectEmployee: (state, action: PayloadAction<string>) => {
            const idx = state.selectedEmployees.indexOf(action.payload);
            if (idx > -1) {
                state.selectedEmployees.splice(idx, 1);
            }
        },

        deselectAllEmployees: (state) => {
            state.selectedEmployees = [];
        },

        setEmployeeCalendarData: (state, action: PayloadAction<RREmployeeType>) => {
            const currentEmployee = action.payload;
            if (currentEmployee.isAdmin || currentEmployee.isSuperAdmin) {
                state.employeeCalendarData = state.calendarData;
                return;
            }

            const newCalendarData: RRCalendarDataType = [];
            for (const calendar of state.calendarData) {
                const newCalendar: RRCalendarDateDataType = {...calendar, items: [], events: []};
                const newItems = calendar.items;
                for (const event of calendar.events) {
                    const isUserIncluded = newItems.find(item => item.employeeId === currentEmployee.id && item.eventId === event.id);
                    if (isUserIncluded) {
                        newCalendarData.push(
                            {
                                ...newCalendar,
                                events: [...newCalendar.events, event],
                                items: newItems.filter(item => item.eventId === event.id)
                            }
                        );
                    }
                }
            }
            state.employeeCalendarData = newCalendarData;
        }

    },
})

export const {
    setDepartment,
    setCalendarFirstDate,
    setCalendarVisibleDays,
    setCurrentCalendarData,
    setEditCalendarEvent,
    setPrintCalendarEvent,
    setReportCalendarEvent,
    setEmployeesPopupVisible,
    setLogisticsPopupVisible,
    setApprovePPHPopupVisible,
    setEventsPopupVisible,
    setPreselectedEvent,
    setCurrentUser,
    setCurrentEmployee,
    selectEmployee,
    deselectEmployee,
    deselectAllEmployees,
    setEmployeeCalendarData,
} = appStateSlice.actions

// Other code such as selectors can use the imported `RootState` type
export const selectCurrentUser = (state: RootState) => state.app.currentUser;
export const selectCurrentEmployee = (state: RootState) => state.app.currentEmployee;
export const selectCalendarFirstDate = (state: RootState) => state.app.calendarFirstDate;
export const selectCalendarVisibleDays = (state: RootState) => state.app.visibleDays;
export const selectCalendarData = (state: RootState) => state.app.calendarData;
export const selectEditCalendarEvent = (state: RootState) => state.app.editCalendarEvent;
export const selectPrintCalendarEvent = (state: RootState) => state.app.printCalendarEvent;
export const selectReportCalendarEvent = (state: RootState) => state.app.reportCalendarEvent;
export const selectEventsPopupVisible = (state: RootState) => state.app.isEventsPopupVisible;
export const selectPreselectedEvent = (state: RootState) => state.app.preselectedEvent;
export const selectEmployeesPopupVisible = (state: RootState) => state.app.isEmployeesPopupVisible;
export const selectLogisticsPopupVisible = (state: RootState) => state.app.isLogisticsPopupVisible;
export const selectApprovePPHPopupVisible = (state: RootState) => state.app.isApprovePPHPopupVisible;
export const selectSelectedEmployees = (state: RootState) => state.app.selectedEmployees;
export const selectDepartment = (state: RootState) => state.app.department;
export const selectDepartmentName = (state: RootState) => departmentNames[state.app.department] || "";

export const selectEmployeeCalendarData = (state: RootState) => state.app.employeeCalendarData;

export const AppStateReducer = appStateSlice.reducer;
